import type {FlattenedJwsInput, JsonWebKeySet, JwsHeaderParameters, KeyLike} from '../types.ts';
import {createLocalJwkSet} from './local.ts';
import {JoseError, JwksTimeout} from '../errors.ts';

export interface RemoteJWKSetOptions {
    /**
     * Timeout (in milliseconds) for the HTTP request. Default is 5000 (5 seconds).
     */
    timeout?: number;
    /**
     * Maximum time (in milliseconds) between successful HTTP requests. Default is 600000 (10 minutes).
     */
    cacheMaxAge: number | typeof Infinity;

    /**
     * Headers to be sent with the HTTP request.
     */
    headers?: Record<string, string>

    // Web API overrides
    fetch?: typeof fetch
    URL?: typeof URL
    Headers?: typeof Headers
}

async function fetchJwks(url: string | URL, headers: Headers, signal: AbortSignal, opts: { fetch?: typeof fetch }) {
    const response = await (opts?.fetch ?? (globalThis.fetch ?? fetch))(url, {
        method: 'GET',
        signal,
        redirect: 'manual',
        headers,
    }).catch ((err) => {
        if (err.name === 'TimeoutError') {
            throw new JwksTimeout();
        }
        throw err;
    });

    if (response.status !== 200) {
        throw new JoseError(`Expected 200 OK from JSON Web Key Set response, got ${response.status} ${response.statusText}`);
    }
    try {
        return await response.json();
    }
    catch (e) {
        throw new JoseError(`Failed to parse the JSON Web Key Set response as JSON: ${e instanceof Error ? e.message : String(e)}`);
    }
}

class RemoteJwksSet{
    readonly #fetch?: typeof fetch;
    readonly #url: URL;
    readonly #timeout: number;
    readonly #cacheMaxAge: number;
    #jwksTimestamp?: number;
    #pendingFetch?: Promise<unknown>;
    readonly #headers: Headers;
    #local!: ReturnType<typeof createLocalJwkSet>;

    constructor(url: URL, options: RemoteJWKSetOptions) {
        this.#fetch = options.fetch;
        this.#url = new (options.URL ?? (globalThis.URL ?? URL))(url.href);
        this.#headers = new (options.Headers ?? (globalThis.Headers ?? Headers))(options.headers);
        this.#timeout = typeof options.timeout === 'number' ? options.timeout : 5000;
        this.#cacheMaxAge = typeof options.cacheMaxAge === 'number' ? options.cacheMaxAge : 600000;
    }

    private fresh() {
        return typeof this.#jwksTimestamp === 'number' ? Date.now() < this.#jwksTimestamp + this.#cacheMaxAge : false;
    }

    jwks(): JsonWebKeySet | undefined {
        // @ts-expect-error dynamic property
        return this.#local?.jwks();
    }

    async getKey(protectedHeader?: JwsHeaderParameters, token?: FlattenedJwsInput): Promise<KeyLike> {
        if (!this.#local || !this.fresh()) {
            await this.reload();
        }
        return await this.#local(protectedHeader, token);
    }

    private async reload() {
        this.#pendingFetch ||= fetchJwks(this.#url.href, this.#headers, AbortSignal.timeout(this.#timeout), { fetch: this.#fetch })
            .then((json) => {
                this.#local = createLocalJwkSet(json as unknown as JsonWebKeySet);
                this.#jwksTimestamp = Date.now();
                this.#pendingFetch = undefined;
            })
            .catch((err) => {
                this.#pendingFetch = undefined;
                throw err;
            });
        await this.#pendingFetch;
    }
}


export function createRemoteJwkSet(jwksUri: URL, options: {
    cacheMaxAge: number,
    timeout?: number,
    fetch?: typeof fetch,
    URL?: typeof URL,
    Headers?: typeof Headers
}) {
    const set = new RemoteJwksSet(jwksUri, options);
    const remoteJwks = async (header?: JwsHeaderParameters, token?: FlattenedJwsInput): Promise<KeyLike> => {
        return set.getKey(header, token);
    };
    Object.defineProperties(remoteJwks, {
        jwks: {
            value: () => structuredClone(set.jwks()),
            enumerable: false,
            configurable: false,
            writable: false,
        }
    });
    return remoteJwks;
}
